<script lang="ts" module>
	import { getClients } from "@api/getClients.js";
	import listPublishedFutureCourseTermsQuery from "@api/queries/listPublishedFutureCourseTerms.graphql";
	import { requestAll } from "@api/requestAll.js";
	import CourseRegistrationModal from "@components/CourseRegistrationModal.svelte";
	import DetailPageLayout from "@components/DetailPageLayout.svelte";
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Carousel from "@components/common/Carousel.svelte";
	import CkEditorContent from "@components/common/CkEditorContent.svelte";
	import Disclosure from "@components/common/Disclosure.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import PriceDisplay from "@components/common/PriceDisplay.svelte";
	import CourseStructuredData from "@components/course/CourseStructuredData.svelte";
	import CourseTermPicker from "@components/course/CourseTermPicker.svelte";
	import CourseTrainerBadge from "@components/course/CourseTrainerBadge.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import CourseReviewsSection from "@components/sections/CourseReviewsSection.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { onMount } from "svelte";
	import clockIcon from "@core/assets/icons/clock.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import type { PublicCourseTerm, PublicCourseTermListParams } from "@core/schema/CourseTerm.js";
	import { CourseType } from "@core/schema/CourseType.js";
	import { Currency } from "@core/schema/Currency.js";
	import { courseTypeContentMap } from "@core/schema/names/courseTypeContentMap.js";
	import { courseTypeNameMap } from "@core/schema/names/courseTypeNameMap.js";
	import { calculateCourseOrderPrice } from "@core/schema/utils/calculateCourseOrderPrice.js";
	import { isTermFull } from "@core/schema/utils/isTermFull.js";
	import { createSortByDate } from "@core/utils/createSortByDate.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { findCourseTrainerByPlace } from "../courseTrainers.js";
	import { courseTypeDescriptionMap } from "../courseTypeDescriptions.js";
	import { getCourseTypeGallery } from "../courseTypeGalleries.js";
	import { selectedCurrency } from "../state/selectedCurrency.svelte.js";
	import translations from "./CourseTypeDetail.translations.json" with { type: "json" };
	import type { CourseTypeDetailProps } from "./CourseTypeDetailProps.js";

	const sortByStartsAt = createSortByDate("startsAt", "asc");

	function pickDefaultTermId(terms: PublicCourseTerm[], preselectedTermId: string | undefined): string | undefined {
		const preselected = preselectedTermId !== undefined ? terms.find(({ id }) => id === preselectedTermId) : undefined;
		return (preselected ?? terms.find(({ capacity, participantsCount }) => participantsCount < capacity))?.id;
	}
</script>

<script lang="ts">
	const { courseType, terms: initialTerms }: CourseTypeDetailProps = $props();
	const translate = getTranslate(translations);
	const { tenant, exchangeRatesSetting } = getGlobalTemplateProps();
	const settings = getGlobalSettings();
	const courseContent = $derived(courseTypeContentMap[courseType]);
	const description = $derived(courseTypeDescriptionMap[courseType][tenant.locale]);
	const gallery = $derived(getCourseTypeGallery(courseType));
	const currency = $derived(selectedCurrency.current ?? tenant.defaultCurrency);

	// Terms are pre-rendered at build time (SSG) so the initial HTML – including structured data –
	// is complete without a client-side fetch. This background refresh only keeps availability current.
	let terms = $derived(initialTerms);
	let descriptionExpanded = $state(false);
	let selectedTermId = $state<string | undefined>();
	let showRegistration = $state(false);
	const selectedTerm = $derived(terms.find(({ id }) => id === selectedTermId));
	const selectedTermTrainer = $derived(selectedTerm && findCourseTrainerByPlace(selectedTerm.placeName));

	const priceTerm = $derived(selectedTerm ?? terms[0]);
	const unitPrice = $derived.by(() => {
		if (priceTerm) {
			return calculateCourseOrderPrice(priceTerm, currency, exchangeRatesSetting?.exchangeRates ?? []);
		}
		if (courseType === CourseType.Workshop) {
			return;
		}
		return calculateCourseOrderPrice({ courseType }, currency, exchangeRatesSetting?.exchangeRates ?? []);
	});
	const price = $derived.by(() => {
		if (!unitPrice) {
			return;
		}
		if (currency === Currency.CZK) {
			return formatPrice(Math.ceil(unitPrice.withTax), currency, tenant.locale, false);
		}
		return formatPrice(unitPrice.withTax, currency, tenant.locale, true);
	});

	const refreshTerms = createPromiseWithStatus(
		async (
			preselectedTermId: string | undefined,
		): Promise<{ terms: PublicCourseTerm[]; preselectedTermId: string | undefined }> => {
			const { graphQL } = await getClients(settings);
			const freshTerms = await requestAll<PublicCourseTerm, PublicCourseTermListParams>(graphQL, {
				query: listPublishedFutureCourseTermsQuery,
				variables: { courseType },
			});
			return { terms: freshTerms.toSorted(sortByStartsAt), preselectedTermId };
		},
		({ terms: freshTerms, preselectedTermId }) => {
			terms = freshTerms;
			if (selectedTermId === undefined || !terms.some(({ id }) => id === selectedTermId)) {
				selectedTermId = pickDefaultTermId(terms, preselectedTermId);
			}
		},
	);
	const termsRefreshing = $derived(refreshTerms.value.status === "initial" || refreshTerms.value.status === "loading");

	onMount(() => {
		const preselectedTermId = new URLSearchParams(window.location.search).get("term") ?? undefined;
		selectedTermId = pickDefaultTermId(terms, preselectedTermId);
		refreshTerms.call(preselectedTermId).catch(() => {
			// Keep the statically generated terms if the background refresh fails.
		});
	});

	function selectTerm(term: PublicCourseTerm): void {
		selectedTermId = term.id;
		const url = new URL(window.location.href);
		url.searchParams.set("term", term.id);
		window.history.replaceState({}, "", url);
	}
</script>

<CourseStructuredData {courseType} {terms} />
<Layout ogImage={gallery[0]}>
	<DetailPageLayout title={courseTypeNameMap[courseType][tenant.locale]}>
		{#snippet content()}
			<p class="flex items-center gap-2 text-fcrgray text-base -mt-1 lg:-mt-2">
				<Icon icon={clockIcon} class="size-4.5" />
				<span><Formatted text={translate("duration", { duration: courseContent.duration[tenant.locale] })} /></span>
			</p>

			<div class="text-base/normal !text-fcrgray">
				<Formatted text={descriptionExpanded ? description.intro : courseContent.shortDescription[tenant.locale]} />

				<button
					type="button"
					class={["underline", descriptionExpanded ? "block mt-4" : "inline-block"]}
					onclick={(): void => {
						descriptionExpanded = !descriptionExpanded;
					}}
				>
					<Formatted text={translate(descriptionExpanded ? "showLess" : "readMore")} />
				</button>
			</div>

			{#if terms.length > 0}
				<CourseTermPicker {terms} {selectedTermId} onselect={selectTerm} />
			{/if}

			{#if unitPrice}
				<PriceDisplay
					withTax={unitPrice.withTax}
					withoutTax={unitPrice.withoutTax}
					taxRate={unitPrice.taxRate}
					{currency}
					locale={tenant.locale}
					withoutTaxLabel={translate("withoutTax")}
				/>
			{/if}
		{/snippet}

		{#snippet form()}
			{#if terms.length > 0}
				<Button
					type="button"
					variant={ButtonVariant.Dark}
					size={ButtonSize.Medium}
					disabled={!selectedTerm || isTermFull(selectedTerm)}
					class="w-full"
					loading={termsRefreshing}
					onclick={(): void => {
						showRegistration = true;
					}}
				>
					<Formatted text={translate("registrationHeading")} />
				</Button>
			{:else}
				<p class="text-fcrgray font-semibold"><Formatted text={translate("noTerms")} /></p>
			{/if}
		{/snippet}

		{#snippet availability()}
			{#if selectedTerm}
				<div class="flex items-center gap-2 text-fcrgray text-base">
					<span class="font-medium"><Formatted text={translate("placeHeading")} />:</span>
					<Link href={selectedTerm.placeUrl} target="_blank" rel="noopener noreferrer" class="underline">
						{selectedTerm.placeName}
					</Link>
				</div>
				{#if selectedTermTrainer}
					<CourseTrainerBadge trainer={selectedTermTrainer} />
				{/if}
			{/if}
		{/snippet}

		{#snippet mobileGallery()}
			<Carousel items={gallery} options={{ loop: true, align: "start" }}>
				{#snippet item(src: string, imageIndex: number)}
					<div class="h-70 relative px-1">
						<img
							{src}
							alt=""
							class="size-full object-cover rounded-2xl"
							loading={imageIndex === 0 ? "eager" : "lazy"}
						/>
					</div>
				{/snippet}
			</Carousel>
		{/snippet}

		{#snippet desktopGallery()}
			{#each gallery as src, index (src)}
				<img {src} alt="" class="w-full object-cover" loading={index === 0 ? "eager" : "lazy"} />
			{/each}
		{/snippet}

		{#snippet disclosures()}
			{#if selectedTerm && selectedTerm.additionalInfo[tenant.locale].length > 0}
				<Disclosure title={translate("additionalInfoHeading")} open={courseType === CourseType.Workshop}>
					<CkEditorContent
						html={selectedTerm.additionalInfo[tenant.locale]}
						class="text-left !text-[inherit] [&_:is(p,strong,em,i)]:!text-base"
					/>
				</Disclosure>
			{/if}
			{#each description.disclosures as disclosure, i (i)}
				<Disclosure title={disclosure.title}>
					<Formatted text={disclosure.content} />
				</Disclosure>
			{/each}
		{/snippet}
	</DetailPageLayout>

	{#if courseType !== CourseType.Workshop}
		<CourseReviewsSection />
	{/if}

	{#if selectedTerm && price !== undefined}
		<CourseRegistrationModal bind:show={showRegistration} term={selectedTerm} {price} />
	{/if}
</Layout>
